import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    filterChipsToSearchParams,
    type VoiceQaFilterChip,
    type VoiceQaSearch,
    voiceQaSearchSchema,
} from '@apps/web/routes/_app/admin/voice-qa/search_params';
import { VoiceQaCallSummary } from '@apps/web/routes/_app/admin/voice-qa/voice_qa_call_summary';
import { VoiceQaListPane } from '@apps/web/routes/_app/admin/voice-qa/voice_qa_list_pane';
import {
    captureVoiceQaSavedViewPayload,
    DEFAULT_VOICE_QA_SORT_BY,
    DEFAULT_VOICE_QA_SORT_ORDER,
    VoiceQaViewPills,
} from '@apps/web/routes/_app/admin/voice-qa/voice_qa_view_pills';
import { useAdminVoiceQaSavedViews } from '@apps/web/swr_data_fetching/admin_voice_qa_views';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import { type VoiceQaSavedViewRead } from '@libraries/typescript/api_client';

const EMPTY_SAVED_VIEWS: ReadonlyArray<VoiceQaSavedViewRead> = [];
const STORAGE_KEY_DEFAULT_VIEW = 'admin-voice-qa-default-view';

export const Route = createFileRoute('/_app/admin/voice-qa/')({
    head: () => ({ meta: [{ title: 'Voice QA | Phoebe Admin' }] }),
    component: VoiceQAPage,
    validateSearch: voiceQaSearchSchema,
});

/**
 * A saved view is only auto-applied on first load when the URL carries no
 * explicit filter/sort state; otherwise a shared link's own params win.
 */
function voiceQaSearchHasExplicitState(search: VoiceQaSearch): boolean {
    const payload = captureVoiceQaSavedViewPayload(search);
    return (
        payload.filters.length > 0 ||
        payload.sort_by !== DEFAULT_VOICE_QA_SORT_BY ||
        payload.sort_order !== DEFAULT_VOICE_QA_SORT_ORDER ||
        search.created_after !== undefined ||
        search.created_before !== undefined ||
        (search.search?.trim() ?? '') !== ''
    );
}

function VoiceQAPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { data: savedViewsResponse, mutate: mutateSavedViews } =
        useAdminVoiceQaSavedViews();
    const { data: currentUser } = useUser({ reason: 'admin-voice-qa' });
    const [activeSavedViewId, setActiveSavedViewId] = useState<string | null>(
        null,
    );
    const [defaultViewId, setDefaultViewId] = useState<string | null>(() => {
        try {
            return window.localStorage.getItem(STORAGE_KEY_DEFAULT_VIEW);
        } catch {
            return null;
        }
    });
    const initializedSavedViewRef = useRef(false);

    const updateSearch = useCallback(
        (
            updates: Partial<VoiceQaSearch>,
            options?: { readonly replace?: boolean },
        ) => {
            navigate({
                search: (current) => ({ ...current, ...updates }),
                replace: options?.replace,
            });
        },
        [navigate],
    );

    const handleApplySavedView = useCallback(
        (savedView: VoiceQaSavedViewRead) => {
            setActiveSavedViewId(savedView.id);
            const chips: Array<VoiceQaFilterChip> =
                savedView.payload.filters.map((filter) => ({
                    dimension: filter.dimension,
                    operator: filter.operator,
                    values: [...filter.values],
                }));
            void navigate({
                replace: true,
                search: (current) => ({
                    ...current,
                    ...filterChipsToSearchParams(chips),
                    created_after: undefined,
                    created_before: undefined,
                    sort_by: savedView.payload.sort_by,
                    sort_order: savedView.payload.sort_order,
                    page: 1,
                }),
            });
        },
        [navigate],
    );

    const handleClearSavedView = useCallback(() => {
        setActiveSavedViewId(null);
        void navigate({
            replace: true,
            search: (current) => ({
                ...current,
                ...filterChipsToSearchParams([]),
                created_after: undefined,
                created_before: undefined,
                sort_by: undefined,
                sort_order: undefined,
                page: 1,
            }),
        });
    }, [navigate]);

    const handleDefaultViewChange = useCallback((viewId: string | null) => {
        setDefaultViewId(viewId);
        try {
            if (viewId === null) {
                window.localStorage.removeItem(STORAGE_KEY_DEFAULT_VIEW);
            } else {
                window.localStorage.setItem(STORAGE_KEY_DEFAULT_VIEW, viewId);
            }
        } catch {
            // Keep the in-memory preference when browser storage is unavailable.
        }
    }, []);

    useEffect(() => {
        if (initializedSavedViewRef.current) {
            return;
        }
        if (voiceQaSearchHasExplicitState(search)) {
            initializedSavedViewRef.current = true;
            return;
        }
        if (savedViewsResponse === undefined) {
            return;
        }

        initializedSavedViewRef.current = true;
        const personalDefaultView =
            defaultViewId === null
                ? undefined
                : savedViewsResponse.views.find(
                      (candidate) => candidate.id === defaultViewId,
                  );
        const defaultView =
            personalDefaultView ??
            savedViewsResponse.views.find(
                (candidate) =>
                    candidate.is_team_default && candidate.scope === 'shared',
            );
        if (defaultView !== undefined) {
            handleApplySavedView(defaultView);
        }
    }, [defaultViewId, handleApplySavedView, savedViewsResponse, search]);

    return (
        <div className="flex flex-col gap-4 p-6">
            <VoiceQaCallSummary />
            <VoiceQaViewPills
                views={savedViewsResponse?.views ?? EMPTY_SAVED_VIEWS}
                search={search}
                activeViewId={activeSavedViewId}
                defaultViewId={defaultViewId}
                currentUserEmail={currentUser?.email ?? null}
                onApplyView={handleApplySavedView}
                onClearView={handleClearSavedView}
                onDefaultViewChange={handleDefaultViewChange}
                onSavedViewsRefresh={mutateSavedViews}
            />
            <VoiceQaListPane search={search} onSearchChange={updateSearch} />
        </div>
    );
}
